import React from 'react';
import {
  Calendar, Clock, Users, Coffee, DollarSign, Bell,
  X, Settings, LogOut, FileText, BarChart3, MapPin, Trophy
} from 'lucide-react';
import NavItem from './NavItem';
import AdminAccountSwitcher from './AdminAccountSwitcher';

// Super admin email for conditional nav items
const SUPER_ADMIN_EMAIL = 'ryan@meetwithreps.com';

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  userType,
  currentView,
  setCurrentView,
  setUserType,
  notifications,
  onLogout,
  currentUser,
  linkedNPIProfile,
  onSwitchAccount
}) => (
  <>
    {/* Mobile overlay */}
    {sidebarOpen && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={() => setSidebarOpen(false)}
      />
    )}
    
    <div
      className={`fixed inset-y-0 left-0 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} w-72 sm:w-64 bg-gray-900 text-white transition-transform duration-300 ease-in-out z-50 lg:translate-x-0 lg:static lg:inset-0`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden ml-auto">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <nav className="space-y-1">
          <div className="px-3 py-2 mb-2">
            <h2 className="text-xl font-bold text-white">RepConnect</h2>
          </div>
          {userType === 'sales' ? (
            <>
              <NavItem 
                icon={<Calendar />} 
                text="Dashboard" 
                onClick={() => { setCurrentView('dashboard'); setSidebarOpen(false); }} 
                active={currentView === 'dashboard'} 
              />
              <NavItem 
                icon={<Users />} 
                text="Find Customers" 
                onClick={() => { setCurrentView('find'); setSidebarOpen(false); }} 
                active={currentView === 'find'} 
              />
              <NavItem 
                icon={<Clock />} 
                text="Calendar" 
                onClick={() => { setCurrentView('meetings'); setSidebarOpen(false); }} 
                active={currentView === 'meetings'} 
              />
              <NavItem
                icon={<Coffee />}
                text="Order Management"
                onClick={() => { setCurrentView('order-management'); setSidebarOpen(false); }}
                active={currentView === 'order-management'}
              />
              <NavItem 
                icon={<DollarSign />} 
                text="Compliance Reports" 
                onClick={() => { setCurrentView('reports'); setSidebarOpen(false); }} 
                active={currentView === 'reports'} 
              />
              <NavItem
                icon={<BarChart3 />}
                text="Workflow Analytics"
                onClick={() => { setCurrentView('workflow-analytics'); setSidebarOpen(false); }}
                active={currentView === 'workflow-analytics'}
              />
              {/* Admin Referrals - Super Admin Only */}
              {currentUser?.email === SUPER_ADMIN_EMAIL && (
                <NavItem
                  icon={<Trophy />}
                  text="Referral Leaderboard"
                  onClick={() => { setCurrentView('admin-referrals'); setSidebarOpen(false); }}
                  active={currentView === 'admin-referrals'}
                />
              )}
            </>
          ) : userType === 'manager' ? (
            <>
              <NavItem 
                icon={<Calendar />} 
                text="Manager Dashboard" 
                onClick={() => { setCurrentView('dashboard'); setSidebarOpen(false); }} 
                active={currentView === 'dashboard'} 
              />
              <NavItem 
                icon={<Users />} 
                text="Team Management" 
                onClick={() => { setCurrentView('reports'); setSidebarOpen(false); }} 
                active={currentView === 'reports'} 
              />
              <NavItem 
                icon={<DollarSign />} 
                text="Team Analytics" 
                onClick={() => { setCurrentView('analytics'); setSidebarOpen(false); }} 
                active={currentView === 'analytics'} 
              />
              <NavItem 
                icon={<BarChart3 />} 
                text="Workflow Analytics" 
                onClick={() => { setCurrentView('workflow-analytics'); setSidebarOpen(false); }} 
                active={currentView === 'workflow-analytics'} 
              />
              <NavItem
                icon={<FileText />}
                text="Reports"
                onClick={() => { setCurrentView('compliance'); setSidebarOpen(false); }}
                active={currentView === 'compliance'}
              />
            </>
          ) : (
            <>
              {/* 1. Dashboard */}
              <NavItem 
                icon={<Calendar />} 
                text="Dashboard" 
                onClick={() => { setCurrentView('dashboard'); setSidebarOpen(false); }} 
                active={currentView === 'dashboard'} 
              />
              
              {/* 2. Calendar */}
              <NavItem
                icon={<FileText />}
                text="Calendar"
                onClick={() => { setCurrentView('meetings'); setSidebarOpen(false); }}
                active={currentView === 'meetings'}
                badge={notifications > 0 ? notifications : null}
              />
              
              {/* 3. Meeting History */}
              <NavItem 
                icon={<Clock />} 
                text="Meeting History" 
                onClick={() => { setCurrentView('history'); setSidebarOpen(false); }} 
                active={currentView === 'history'} 
              />
              
              {/* 4. Dining Preferences */}
              <NavItem 
                icon={<Coffee />} 
                text="Dining Preferences" 
                onClick={() => { setCurrentView('preferences'); setSidebarOpen(false); }} 
                active={currentView === 'preferences'} 
              />
              
              {/* 5. Practice Locations */}
              <NavItem
                icon={<MapPin />}
                text="Practice Locations"
                onClick={() => { setCurrentView('customer-locations'); setSidebarOpen(false); }}
                active={currentView === 'customer-locations'}
              />

              {/* 6. Availability */}
              <NavItem
                icon={<Clock />}
                text="Availability"
                onClick={() => { setCurrentView('availability'); setSidebarOpen(false); }}
                active={currentView === 'availability'}
              />

              {/* 7. Team Management */}
              <NavItem 
                icon={<Users />} 
                text="Team Management" 
                onClick={() => { setCurrentView('team-management'); setSidebarOpen(false); }} 
                active={currentView === 'team-management'} 
              />
            </>
          )}
        </nav>
      </div>
      
      <div className="mt-auto p-4 border-t border-gray-800" style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
        {/* User Info */}
        {currentUser && (
          <div className="mb-4 p-3 bg-gray-800 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                {currentUser.picture ? (
                  <img 
                    src={currentUser.picture} 
                    alt={currentUser.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <span className="text-white font-semibold">
                    {(currentUser.fullName || currentUser.name || 'User').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {currentUser.fullName || currentUser.name || 'User'}
                </p>
                <p className="text-xs text-gray-300 truncate">
                  {currentUser.email}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Admin Account Switcher - only visible to super admin */}
        {onSwitchAccount && (
          <div className="px-3 py-2">
            <AdminAccountSwitcher
              currentUser={currentUser}
              onSwitchAccount={onSwitchAccount}
            />
          </div>
        )}

        <NavItem
          icon={<Settings />}
          text="Settings"
          onClick={() => { setCurrentView('settings'); setSidebarOpen(false); }}
        />
        <NavItem
          icon={<LogOut />}
          text="Logout"
          onClick={() => {
            if (onLogout) {
              onLogout();
            }
            setSidebarOpen(false);
          }}
        />
      </div>
      </div>
    </div>
  </>
);

export default Sidebar;